import type { ObjectWorkspaceWorkbenchContext } from '@heidi/agents';
import { clearComposerDraftStorage } from '@heidi/agents/data';
import { useCallback, useEffect, useRef, useState, type JSX } from 'react';
import { useIntl } from 'react-intl';

import { ObjectWorkspaceChatHarnessView } from './object-workspace-chat-harness-view';
import { objectWorkspaceChatHarnessMessages as messages } from './object-workspace-chat-harness.messages';
import {
  emptyObjectWorkspaceChatTab,
  focusActiveWorkTab,
  isObjectWorkspaceChatTabProtected,
  normalizedObjectWorkspaceTabTitle,
  objectWorkspaceComposerDraftStorageKey,
  type ObjectWorkspaceChatHarnessState,
} from './object-workspace-chat-utils';
import {
  type ObjectWorkspaceChatTab,
  type ObjectWorkspaceChatTabLifecycle,
} from './object-workspace-project-chat-tab';
import { useObjectWorkspacePromptTabs } from './use-object-workspace-prompt-tabs';

export interface ObjectWorkspaceChatLifecycleReport {
  objectKey: string;
  hasQueuedTurns: boolean;
  isOpeningTurnInFlight: boolean;
  hasUnsentDraft: boolean;
  discardPendingOrAll: () => void;
  cancelPendingDiscard: () => void;
}

export type ObjectWorkspaceDiscardRequest = (proceed: () => void) => void;

interface ObjectWorkspaceChatHarnessProps extends ObjectWorkspaceWorkbenchContext {
  showRunOptions: boolean;
  onLifecycleChange: (lifecycle: ObjectWorkspaceChatLifecycleReport) => void;
  requestDiscard: ObjectWorkspaceDiscardRequest;
  /** Session the revealed context panel is already showing, when it has one. */
  openArtifactSessionId?: string;
  /** Restores the hidden object pane. Omitted while the pane is showing. */
  onExpandObjectPane?: () => void;
  onOpenLastSession: (sessionId: string) => void;
}

export function ObjectWorkspaceChatHarness({
  object,
  anchorSelection,
  taskSelection,
  projectSelection,
  clearTaskSelection,
  newTabTitle,
  showRunOptions,
  onLifecycleChange,
  requestDiscard,
  openArtifactSessionId,
  onExpandObjectPane,
  onOpenLastSession,
}: ObjectWorkspaceChatHarnessProps): JSX.Element {
  const intl = useIntl();
  const discardedTabIds = useRef(new Set<string>());
  const pendingDiscardTabId = useRef<string>(undefined);
  const initialTabId = `${object.kind}-${object.id}-heidi`;
  const [state, setState] = useState<ObjectWorkspaceChatHarnessState>(() => ({
    activeTabId: initialTabId,
    tabs: [
      emptyObjectWorkspaceChatTab(
        initialTabId,
        intl.formatMessage(messages.heidiTab),
        'heidi'
      ),
    ],
  }));
  const stateRef = useRef(state);
  stateRef.current = state;
  const objectKey = `${object.kind}:${object.id}`;

  const announce = useCallback(
    (
      current: ObjectWorkspaceChatHarnessState,
      next: Omit<ObjectWorkspaceChatHarnessState, 'announcement'>,
      text: string
    ): ObjectWorkspaceChatHarnessState => ({
      ...next,
      announcement: { id: (current.announcement?.id ?? 0) + 1, text },
    }),
    []
  );

  const discardTab = useCallback(
    (tabId: string) => {
      discardedTabIds.current.add(tabId);
      clearComposerDraftStorage(objectWorkspaceComposerDraftStorageKey(object, tabId));
    },
    [object]
  );
  const discardAll = useCallback(() => {
    stateRef.current.tabs.forEach((tab) => discardTab(tab.id));
  }, [discardTab]);
  const discardPendingOrAll = useCallback(() => {
    const tabId = pendingDiscardTabId.current;
    pendingDiscardTabId.current = undefined;
    if (tabId) discardTab(tabId);
    else discardAll();
  }, [discardAll, discardTab]);
  const cancelPendingDiscard = useCallback(() => {
    pendingDiscardTabId.current = undefined;
  }, []);

  const hasQueuedTurns = state.tabs.some((tab) => tab.hasQueuedTurns);
  const isOpeningTurnInFlight = state.tabs.some(
    (tab) => tab.isOpeningTurnInFlight || tab.isTurnInFlight
  );
  const hasUnsentDraft = state.tabs.some((tab) => !tab.isDraftEmpty);
  useEffect(() => {
    onLifecycleChange({
      objectKey,
      hasQueuedTurns,
      isOpeningTurnInFlight,
      hasUnsentDraft,
      discardPendingOrAll,
      cancelPendingDiscard,
    });
  }, [
    cancelPendingDiscard,
    discardPendingOrAll,
    hasQueuedTurns,
    hasUnsentDraft,
    isOpeningTurnInFlight,
    objectKey,
    onLifecycleChange,
  ]);

  const { addEmptyTab, takeTaskLaunches } = useObjectWorkspacePromptTabs({
    object,
    anchorSelection,
    taskSelection,
    projectSelection,
    clearTaskSelection,
    newTabTitle,
    announce,
    setState,
  });

  const bindProject = useCallback((tabId: string, projectId: string): boolean => {
    if (discardedTabIds.current.has(tabId)) return false;
    let accepted = false;
    setState((current) => {
      if (!current.tabs.some((tab) => tab.id === tabId)) return current;
      accepted = true;
      return {
        ...current,
        tabs: current.tabs.map((tab) => (tab.id === tabId ? { ...tab, projectId } : tab)),
      };
    });
    return accepted;
  }, []);
  const updateLifecycle = useCallback(
    (tabId: string, lifecycle: ObjectWorkspaceChatTabLifecycle) => {
      setState((current) => {
        const target = current.tabs.find((tab) => tab.id === tabId);
        if (!target) return current;
        const unchanged = Object.entries(lifecycle).every(
          ([key, value]) => target[key as keyof ObjectWorkspaceChatTab] === value
        );
        if (unchanged) return current;
        return {
          ...current,
          tabs: current.tabs.map((tab) =>
            tab.id === tabId ? { ...tab, ...lifecycle } : tab
          ),
        };
      });
    },
    []
  );

  const updateTitleForSubmission = useCallback(
    (tabId: string, prompt: string) => {
      setState((current) => {
        const currentTab = current.tabs.find((tab) => tab.id === tabId);
        if (!currentTab) return current;
        const title =
          currentTab.title === newTabTitle
            ? normalizedObjectWorkspaceTabTitle(prompt, newTabTitle)
            : currentTab.title;
        return announce(
          current,
          {
            ...current,
            tabs: current.tabs.map((tab) => (tab.id === tabId ? { ...tab, title } : tab)),
          },
          intl.formatMessage(messages.submissionStarted, { title })
        );
      });
    },
    [announce, intl, newTabTitle]
  );
  const announceSubmissionError = useCallback(
    (tabId: string) => {
      setState((current) => {
        const tab = current.tabs.find((candidate) => candidate.id === tabId);
        return tab
          ? announce(
              current,
              current,
              intl.formatMessage(messages.submissionFailed, { title: tab.title })
            )
          : current;
      });
    },
    [announce, intl]
  );

  const performDismissTab = useCallback(
    (tabId: string) => {
      setState((current) => {
        const dismissedIndex = current.tabs.findIndex((tab) => tab.id === tabId);
        if (dismissedIndex === -1) return current;
        const dismissed = current.tabs[dismissedIndex]!;
        if (dismissed.kind === 'heidi') return current;
        const tabs = current.tabs.filter((tab) => tab.id !== tabId);
        const activeTabId =
          current.activeTabId === tabId
            ? (tabs[Math.min(dismissedIndex, tabs.length - 1)]?.id ?? tabs[0]!.id)
            : current.activeTabId;
        const nextActive = tabs.find((tab) => tab.id === activeTabId)!;
        return announce(
          current,
          { tabs, activeTabId },
          intl.formatMessage(
            current.activeTabId === tabId
              ? messages.tabClosedAndSelected
              : messages.tabClosed,
            current.activeTabId === tabId
              ? { title: dismissed.title, nextTitle: nextActive.title }
              : { title: dismissed.title }
          )
        );
      });
      focusActiveWorkTab();
    },
    [announce, intl]
  );
  const dismissTab = useCallback(
    (tabId: string) => {
      const tab = stateRef.current.tabs.find((candidate) => candidate.id === tabId);
      if (!tab) return;
      if (!isObjectWorkspaceChatTabProtected(tab)) {
        discardTab(tabId);
        performDismissTab(tabId);
        return;
      }
      pendingDiscardTabId.current = tabId;
      requestDiscard(() => {
        pendingDiscardTabId.current = undefined;
        discardTab(tabId);
        performDismissTab(tabId);
      });
    },
    [discardTab, performDismissTab, requestDiscard]
  );
  const selectTab = useCallback(
    (activeTabId: string) => {
      setState((current) => {
        const selected = current.tabs.find((tab) => tab.id === activeTabId);
        return selected
          ? announce(
              current,
              { ...current, activeTabId },
              intl.formatMessage(messages.tabSelected, { title: selected.title })
            )
          : current;
      });
    },
    [announce, intl]
  );
  const sourceSessionId = object.kind === 'visit' ? object.id : object.sourceSessionId;
  return (
    <ObjectWorkspaceChatHarnessView
      object={object}
      tabs={state.tabs}
      activeTabId={state.activeTabId}
      announcement={state.announcement}
      showRunOptions={showRunOptions}
      sourceSessionId={sourceSessionId}
      isLastSessionOpen={!!sourceSessionId && sourceSessionId === openArtifactSessionId}
      onExpandObjectPane={onExpandObjectPane}
      onOpenLastSession={onOpenLastSession}
      onTabSelect={selectTab}
      onAddTab={addEmptyTab}
      onDismissTab={dismissTab}
      onBindProject={bindProject}
      onLifecycleChange={updateLifecycle}
      onSubmissionError={announceSubmissionError}
      onSubmissionStart={updateTitleForSubmission}
      onTaskLaunchesTaken={takeTaskLaunches}
    />
  );
}
